import TeamValueChanges from "./TeamValueChanges";
import { settingChanges } from "../helpers/teamSettingValues";
import React from "react";
import { useTranslation } from "react-i18next";
import { futureSummary, changedFields } from "../helpers/teamAnalysis";
import { homeAdvantage, strengthRanking } from "../helpers/teamSettingsImpact";
import { referenceAudit } from "./TeamModelReferences";
export default function TeamSettingsImpact({
  data,
  draft,
  stat,
  ranking,
  parameters,
  pending,
  en,
  names,
  logo,
  ids,
  fieldLabel,
  normalization,
  probabilities,
}) {
  const { i18n } = useTranslation();
  const statisticName = (key) =>
    key === "conversion"
      ? "Conversion"
      : i18n.t(
          "help.automation.business.statisticNames." +
            stat?.registry?.[key]?.title,
          { defaultValue: stat?.registry?.[key]?.title || key }
        );
  const t = (d, e) => (en ? e : d),
    fmt = (n) =>
      Number.isFinite(n)
        ? n.toLocaleString(en ? "en-GB" : "de-DE", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 3,
          })
        : "—";
  const result = strengthRanking(draft),
    before = strengthRanking(data.result_teams);
  const fields = [
    "total_strength",
    "total_goals",
    "for_total",
    "against_total",
    "for_home",
    "against_home",
    "for_away",
    "against_away",
    "for_homefactor",
    "against_homefactor",
    "draw_factor",
  ];
  const statBefore = data.ranking || [];
  const referenceBefore = referenceAudit(data.state),
    referenceAfter = referenceAudit(stat);
  const statFmt = (key, n) =>
    key === "conversion" || Number(key) === 45
      ? Number.isFinite(n)
        ? fmt(n * 100) + " %"
        : "—"
      : fmt(n);
  const value = (a, b, key) => (
    <>
      {statFmt(key, a)} → <strong>{statFmt(key, b)}</strong>
    </>
  );
  return (
    <section
      className="ta-settings-impact"
      aria-label={t("Auswirkungen des Entwurfs", "Draft impact")}
    >
      <h3>
        {t(
          "Dauerhaft zu schreibende Einzelwerte",
          "Individual values to be saved"
        )}
      </h3>
      <TeamValueChanges
        registry={data.state?.registry}
        rows={settingChanges(data, draft, stat)}
        {...{ en, names }}
      />
      <h3>{t("Gespeichert → Entwurf", "Saved → draft")}</h3>
      <div className="ta-scroll">
        <table>
          <thead>
            <tr>
              <th>{t("Team / Kennzahl", "Team / metric")}</th>
              <th>{t("Ergebnismodell", "Results model")}</th>
              <th>{t("Statistikmodell", "Statistics model")}</th>
            </tr>
          </thead>
          <tbody>
            {ids.map((id) => {
              const old = futureSummary(data.result_teams[id]),
                next = futureSummary(draft[id]);
              const so = futureSummary(
                  statBefore.find((r) => r.team_id === Number(id))
                ),
                sn = futureSummary(
                  ranking.find((r) => r.team_id === Number(id))
                );
              return (
                <React.Fragment key={id}>
                  <tr>
                    <th colSpan={3}>
                      {logo(id)} {names(id)}
                    </th>
                  </tr>
                  {fields.map((k) => (
                    <tr key={k}>
                      <th>{fieldLabel(k)}</th>
                      <td>{value(old?.[k], next?.[k])}</td>
                      <td>
                        {k === "draw_factor"
                          ? value(old?.draw_factor, next?.draw_factor)
                          : value(so?.[k], sn?.[k])}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <th>
                      {t(
                        "Heimvorteil · offensiv / defensiv",
                        "Home advantage · attack / defence"
                      )}
                    </th>
                    <td>{value(homeAdvantage(old), homeAdvantage(next))}</td>
                    <td>{value(homeAdvantage(so), homeAdvantage(sn))}</td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {normalization && (
        <details open>
          <summary>
            {t(
              "Zusätzliche Normierung · drei Zustände",
              "Additional normalization · three states"
            )}
          </summary>
          <div className="ta-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t("Wert", "Value")}</th>
                  <th>{t("Gespeichert", "Saved")}</th>
                  <th>
                    {t(
                      "Team-Entwurf vor Normierung",
                      "Team draft before normalization"
                    )}
                  </th>
                  <th>
                    {t(
                      "Mit Normierung · Entwurf",
                      "With normalization · draft"
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {["stored_goal_average", "exp_home_factor", "drawfactor"].map(
                  (k) => (
                    <tr key={k}>
                      <th>{fieldLabel(k)}</th>
                      <td>{fmt(data.result_parameters?.[k])}</td>
                      <td>{fmt(normalization.parameters?.[k])}</td>
                      <td>{pending ? "…" : fmt(parameters?.[k])}</td>
                    </tr>
                  )
                )}
                {Object.keys(draft).flatMap((id) =>
                  [
                    "total_strength",
                    "total_goals",
                    "for_home",
                    "against_home",
                    "for_away",
                    "against_away",
                  ].map((k) => (
                    <tr key={id + k}>
                      <th>
                        {names(id)} · {fieldLabel(k)}
                      </th>
                      <td>{fmt(futureSummary(data.result_teams[id])?.[k])}</td>
                      <td>
                        {fmt(futureSummary(normalization.draft[id])?.[k])}
                      </td>
                      <td>{fmt(futureSummary(draft[id])?.[k])}</td>
                    </tr>
                  ))
                )}
                {referenceAfter.flatMap((r) =>
                  r.values.map((v, i) => (
                    <tr key={r.key + i}>
                      <th>
                        {r.key} ·{" "}
                        {fieldLabel(
                          [
                            "home_for",
                            "home_against",
                            "away_for",
                            "away_against",
                          ][i]
                        )}
                      </th>
                      <td>
                        {fmt(
                          referenceBefore.find((x) => x.key === r.key)?.values[
                            i
                          ]?.stored
                        )}
                      </td>
                      <td>
                        {fmt(
                          referenceAudit(normalization.stat).find(
                            (x) => x.key === r.key
                          )?.values[i]?.stored
                        )}
                      </td>
                      <td>{fmt(v.stored)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </details>
      )}
      <details open>
        <summary>
          {t(
            "Season Parameters · beide Modelle",
            "Season parameters · both models"
          )}
          {pending ? " …" : ""}
        </summary>
        <p>
          {t(
            "Entwurf aus aktuellen Teammitteln. Keine automatische Normierung. Die Statistikreferenzen werden durch die vorhandene Modellroutine neu berechnet.",
            "Draft from current team means. No automatic normalization. Statistics references are recalculated by the existing model routine."
          )}
        </p>
        <div className="ta-scroll">
          <table>
            <thead>
              <tr>
                <th>
                  {t(
                    "Ergebnismodell / gemeinsamer Wahrscheinlichkeitsparameter",
                    "Results model / shared probability parameter"
                  )}
                </th>
                <th>{t("Gespeichert", "Saved")}</th>
                <th>{t("Entwurf", "Draft")}</th>
              </tr>
            </thead>
            <tbody>
              {["stored_goal_average", "exp_home_factor", "drawfactor"].map(
                (k) => (
                  <tr key={k}>
                    <th>{fieldLabel(k)}</th>
                    <td>{fmt(data.result_parameters?.[k])}</td>
                    <td>{pending ? "…" : fmt(parameters?.[k])}</td>
                  </tr>
                )
              )}
              {[
                "for_home",
                "against_home",
                "for_away",
                "against_away",
                "draw_factor",
              ].map((k) => (
                <tr key={k}>
                  <th>
                    {t("Teammittel", "Team mean")} · {fieldLabel(k)}
                  </th>
                  <td>{fmt(data.result_parameters?.team_means?.[k])}</td>
                  <td>{pending ? "…" : fmt(parameters?.team_means?.[k])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ta-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("Statistikreferenz", "Statistics reference")}</th>
                {["home_for", "home_against", "away_for", "away_against"].map(
                  (k) => (
                    <th key={k}>{fieldLabel(k)}</th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {referenceAfter.map((r) => (
                <tr key={r.key}>
                  <th>
                    {r.key === "conversion"
                      ? "Conversion"
                      : statisticName(r.key)}
                  </th>
                  {r.values.map((v, i) => (
                    <td key={i}>
                      {value(
                        referenceBefore.find((b) => b.key === r.key)?.values[i]
                          ?.stored,
                        v.stored,
                        r.key
                      )}
                      {!v.matches ? " ⚠" : ""}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
      <details open>
        <summary>
          {t(
            "Torerwartung & Wahrscheinlichkeiten · Heimspiel gegen Ligamittel",
            "Expected goals & probabilities · home match against league mean"
          )}
        </summary>
        <small>
          {t(
            "Aktuelle Modellrechnung zur Einordnung des Entwurfs, keine historische Neuberechnung.",
            "Current model calculation to inspect the draft, not a historical recalculation."
          )}
        </small>
        <div className="ta-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("Team / Modell", "Team / model")}</th>
                <th>{t("Für", "For")}</th>
                <th>{t("Gegen", "Against")}</th>
                <th>{t("Sieg %", "Win %")}</th>
                <th>{t("Remis %", "Draw %")}</th>
                <th>{t("Niederlage %", "Loss %")}</th>
              </tr>
            </thead>
            <tbody>
              {ids.flatMap((id) =>
                ["result", "statistics"].map((model) => {
                  const a = probabilities?.before?.[id]?.[model],
                    b = probabilities?.after?.[id]?.[model];
                  return (
                    <tr key={id + model}>
                      <th>
                        {names(id)} ·{" "}
                        {model === "result"
                          ? t("Ergebnismodell", "Results model")
                          : t("Statistikmodell", "Statistics model")}
                      </th>
                      {[
                        "home",
                        "away",
                        "homewinprob",
                        "drawprob",
                        "awaywinprob",
                      ].map((k) => (
                        <td key={k}>
                          {pending
                            ? "…"
                            : value(
                                a?.[k] == null
                                  ? null
                                  : a[k] * (k.endsWith("prob") ? 100 : 1),
                                b?.[k] == null
                                  ? null
                                  : b[k] * (k.endsWith("prob") ? 100 : 1)
                              )}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </details>
      <details open>
        <summary>
          {t(
            "Liga-Stärkeranking · laufender Entwurf",
            "League strength ranking · live draft"
          )}
        </summary>
        <div className="ta-models">
          {["result", "statistics"].map((model) => {
            const rows =
              model === "result"
                ? result
                : ranking.map((r) => ({ ...r, id: r.team_id }));
            const olds =
              model === "result"
                ? before
                : statBefore.map((r) => ({ ...r, id: r.team_id }));
            return (
              <div key={model} className="ta-scroll">
                <h4>
                  {model === "result"
                    ? t("Ergebnismodell", "Results model")
                    : t("Statistikmodell", "Statistics model")}
                </h4>
                <table>
                  <thead>
                    <tr>
                      <th>{t("Mannschaft", "Team")}</th>
                      <th>{t("Rang", "Rank")}</th>
                      <th>{t("Quotient", "Ratio")}</th>
                      <th>{t("Torschnitt", "Goals/game")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const o = olds.find((x) => x.id === r.id);
                      return (
                        <tr
                          key={r.id}
                          className={
                            ids.map(Number).includes(r.id)
                              ? "ta-selected-ranking"
                              : ""
                          }
                        >
                          <th>
                            {logo(r.id)} {names(r.id)}
                          </th>
                          <td>
                            {o?.rank ?? "—"} → {r.rank}
                          </td>
                          <td>{value(o?.total_strength, r.total_strength)}</td>
                          <td>{value(o?.total_goals, r.total_goals)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      </details>
      <details>
        <summary>
          {t(
            "Alle geänderten Statistik-Einzelwerte",
            "All changed individual statistics"
          )}
        </summary>
        <div className="ta-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("Mannschaft / Statistik", "Team / statistic")}</th>
                <th>{t("Wert", "Value")}</th>
                <th>{t("Gespeichert → Entwurf", "Saved → draft")}</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(stat?.teams || {}).flatMap(([id, team]) =>
                [...Object.keys(team.statistics || {}), "conversion"].flatMap(
                  (key) => {
                    const old =
                      key === "conversion"
                        ? data.state?.teams[id]?.conversion
                        : data.state?.teams[id]?.statistics[key];
                    const next =
                      key === "conversion"
                        ? team.conversion
                        : team.statistics[key];
                    return changedFields(old, next).map((c) => (
                      <tr key={id + key + c.field}>
                        <th>
                          {names(id)} ·{" "}
                          {key === "conversion"
                            ? "Conversion"
                            : statisticName(key)}
                        </th>
                        <td>{fieldLabel(c.field)}</td>
                        <td>{value(c.before, c.after, key)}</td>
                      </tr>
                    ));
                  }
                )
              )}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}
